import { useCallback, useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { MessageCircle, RefreshCw, AlertTriangle, Wifi, Send, IndianRupee, Layers } from "lucide-react";
import { toast } from "sonner";

function fmtDate(iso, short = false) {
  if (!iso) return "—";
  try {
    const opts = short
      ? { day: "2-digit", month: "short", year: "2-digit" }
      : { day: "numeric", month: "short", year: "numeric", hour: "numeric", minute: "2-digit" };
    return new Date(iso).toLocaleString("en-IN", opts);
  } catch { return iso; }
}

export const WccBalanceCard = ({ canSync = true }) => {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [balance, setBalance] = useState("");
  const [cost, setCost] = useState("");
  const [plan, setPlan] = useState("Basic");
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get("/wcc/balance").then((r) => setData(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const openSync = () => {
    setBalance(data?.configured ? String(data.balance) : "");
    setCost(data?.per_message_cost ? String(data.per_message_cost) : "");
    setPlan(data?.plan || "Basic");
    setOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.post("/wcc/sync", { balance: parseFloat(balance) || 0, per_message_cost: parseFloat(cost) || 0, plan: plan || "Basic" });
      setData(r.data);
      setOpen(false);
      toast.success("WCC balance synced.");
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSaving(false); }
  };

  if (!data) return null;
  const configured = data.configured;

  return (
    <div className="flex flex-col lg:flex-row gap-6 items-start" data-testid="wcc-balance-card">
      {/* ------- debit-card visual ------- */}
      <div className="relative w-full max-w-md shrink-0 aspect-[8/5] rounded-3xl overflow-hidden shadow-2xl select-none" data-testid="wcc-debit-card">
        <div className="absolute inset-0 bg-gradient-to-br from-[#053b2d] via-[#075e45] to-[#02251b]" />
        <div className="absolute -right-16 -top-16 h-60 w-60 rounded-full bg-emerald-400/10" />
        <div className="absolute -right-6 top-28 h-40 w-40 rounded-full bg-teal-300/10" />
        <div className="absolute -left-10 -bottom-16 h-44 w-44 rounded-full bg-emerald-300/5" />

        <div className="relative h-full p-5 sm:p-6 flex flex-col text-white">
          {/* top row: brand + plan */}
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-lg bg-white/15 flex items-center justify-center">
                <MessageCircle className="h-4.5 w-4.5 text-emerald-300" style={{ height: 18, width: 18 }} />
              </div>
              <div>
                <div className="text-sm font-bold tracking-[0.18em]">AISENSY</div>
                <div className="text-[9px] uppercase tracking-[0.22em] text-emerald-200/80">WhatsApp Credits</div>
              </div>
            </div>
            <span className="rounded-full bg-emerald-400/20 border border-emerald-300/40 text-emerald-100 text-[10px] font-bold uppercase tracking-widest px-2.5 py-1" data-testid="wcc-card-plan">
              {(data.plan || "Basic")} Plan
            </span>
          </div>

          {/* chip + contactless */}
          <div className="flex items-center gap-3 mt-4">
            <div className="h-8 w-11 rounded-md bg-gradient-to-br from-amber-200 via-yellow-400 to-amber-500 relative overflow-hidden">
              <div className="absolute inset-x-0 top-1/2 h-px bg-amber-700/50" />
              <div className="absolute inset-y-0 left-1/3 w-px bg-amber-700/50" />
              <div className="absolute inset-y-0 right-1/3 w-px bg-amber-700/50" />
            </div>
            <Wifi className="h-5 w-5 rotate-90 text-white/50" />
            {configured && data.low && (
              <span className="ml-auto inline-flex items-center gap-1 rounded-full bg-rose-500/90 text-white text-[10px] font-bold px-2 py-0.5 animate-pulse" data-testid="wcc-low-badge">
                <AlertTriangle className="h-3 w-3" /> LOW BALANCE
              </span>
            )}
          </div>

          {/* balance (card-number position) */}
          <div className="mt-auto">
            <div className="text-[10px] uppercase tracking-[0.25em] text-emerald-200/70">WCC Credits Balance</div>
            <div className="text-3xl sm:text-4xl font-bold tracking-wide" style={{ fontFamily: "'Courier New', monospace" }} data-testid="wcc-balance-value">
              {configured ? `₹${Number(data.balance).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "₹ ——.——"}
            </div>
            <div className="text-xs text-emerald-100/80 mt-0.5" data-testid="wcc-msgs-left">
              {configured && data.estimated_messages_left !== null
                ? `≈ ${data.estimated_messages_left.toLocaleString("en-IN")} messages left`
                : configured ? "Set cost/message to estimate messages left" : "Not synced yet"}
            </div>
          </div>

          {/* bottom row: synced-by (cardholder) + last sync (expiry) */}
          <div className="flex items-end justify-between mt-4">
            <div>
              <div className="text-[9px] uppercase tracking-[0.2em] text-emerald-200/60">Synced by</div>
              <div className="text-xs font-semibold uppercase tracking-wider">{data.synced_by || "—"}</div>
            </div>
            <div className="text-right">
              <div className="text-[9px] uppercase tracking-[0.2em] text-emerald-200/60">Last sync</div>
              <div className="text-xs font-semibold uppercase tracking-wider">{fmtDate(data.synced_at, true)}</div>
            </div>
          </div>
        </div>
      </div>

      {/* ------- details + actions ------- */}
      <div className="flex-1 w-full space-y-4">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="wcc-stats">
          {[
            { icon: Send, label: "Messages left", value: configured && data.estimated_messages_left !== null ? data.estimated_messages_left.toLocaleString("en-IN") : "—" },
            { icon: IndianRupee, label: "Cost / message", value: data.per_message_cost > 0 ? `₹${data.per_message_cost}` : "—" },
            { icon: MessageCircle, label: "Sent since sync", value: data.messages_since_sync.toLocaleString("en-IN") },
            { icon: Layers, label: "Plan", value: data.plan || "Basic" },
          ].map((s) => (
            <div key={s.label} className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-3">
              <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-slate-500 dark:text-slate-400">
                <s.icon className="h-3.5 w-3.5" /> {s.label}
              </div>
              <div className="text-lg font-bold text-slate-900 dark:text-slate-100 mt-1">{s.value}</div>
            </div>
          ))}
        </div>

        <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/60 p-4 flex flex-wrap items-center gap-3">
          <p className="text-xs text-slate-500 dark:text-slate-400 flex-1 min-w-[220px]">
            AiSensy has no public balance API, so this is a <span className="font-semibold">tracked estimate</span>:
            sync the real balance from your AiSensy dashboard and the app auto-deducts the per-message cost
            on every WhatsApp message it sends.
          </p>
          {canSync && (
            <Button onClick={openSync} className="gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="wcc-sync-btn">
              <RefreshCw className="h-3.5 w-3.5" /> {configured ? "Sync balance" : "Set up tracking"}
            </Button>
          )}
        </div>
      </div>

      {/* ------- sync dialog ------- */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md" data-testid="wcc-sync-dialog">
          <DialogHeader>
            <DialogTitle>Sync WCC balance</DialogTitle>
            <DialogDescription>
              Enter the current WCC balance from your AiSensy dashboard.
              The app will auto-deduct the per-message cost for every WhatsApp message it sends.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-1">
            <div className="space-y-1.5">
              <Label htmlFor="wcc-balance">Current WCC balance (₹)</Label>
              <Input id="wcc-balance" type="number" min="0" step="0.01" value={balance} onChange={(e) => setBalance(e.target.value)} placeholder="e.g. 1500" data-testid="wcc-sync-balance-input" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="wcc-cost">Cost per message (₹)</Label>
              <Input id="wcc-cost" type="number" min="0" step="0.01" value={cost} onChange={(e) => setCost(e.target.value)} placeholder="e.g. 0.88" data-testid="wcc-sync-cost-input" />
              <p className="text-xs text-slate-500">Utility conversation rate on your AiSensy plan (₹0.115–₹0.99 typically).</p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="wcc-plan">AiSensy plan name</Label>
              <Input id="wcc-plan" value={plan} onChange={(e) => setPlan(e.target.value)} placeholder="e.g. Basic" data-testid="wcc-sync-plan-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving || balance === ""} data-testid="wcc-sync-save">
              {saving ? "Saving…" : "Save balance"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WccBalanceCard;
